import React, { useState } from 'react';
import {
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import { confirmAction } from '../../../utils/confirm';
import useAsyncAction from '../../../Components/Hooks/useAsyncAction';
import ActionButton from '../../../Components/Common/ActionButton';
import RespondNotesEditor from '../ui/RespondNotesEditor';

const hasHtmlText = value =>
  String(value ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim().length > 0;

// finalValuePrefill (string) = free-text item: Agree & Lock asks for the Final
// value (prefilled by caller) and sends it as onLock(true, value).
const AgreeLockButton = ({
  isLocked = false,
  onLock,
  disabled = false,
  finalValuePrefill,
  finalValue,
}) => {
  const canToggle = typeof onLock === 'function';
  const requiresFinalValue = typeof finalValuePrefill === 'string';
  const [isFinalOpen, setIsFinalOpen] = useState(false);
  const [finalDraft, setFinalDraft] = useState('');

  const handleAgree = async () => {
    if (requiresFinalValue) {
      setFinalDraft(finalValuePrefill);
      setIsFinalOpen(true);
      return;
    }
    const confirmed = await confirmAction({
      tone: 'primary',
      title: 'Agree and lock this item?',
      message:
        'This marks the item as agreed, so no further clarification will be needed. You can unlock it again later.',
      confirmLabel: 'Agree and lock',
    });
    if (!confirmed) return;
    await onLock(true);
  };

  const handleUnlock = async () => {
    const confirmed = await confirmAction({
      tone: 'warning',
      title: 'Unlock this agreement?',
      message:
        'The item will need to be agreed or responded to again before you can send clarification.',
      confirmLabel: 'Unlock agreement',
    });
    if (!confirmed) return;
    await onLock(false);
  };

  const [handleToggle, isToggling] = useAsyncAction(async () => {
    if (isLocked) {
      await handleUnlock();
      return;
    }
    await handleAgree();
  });

  const [handleSaveFinal, isSavingFinal] = useAsyncAction(async () => {
    if (!hasHtmlText(finalDraft)) return;
    const result = await onLock(true, finalDraft);
    if (result !== false) setIsFinalOpen(false);
  });

  if (!canToggle && !isLocked) return null;

  const isInteractive = canToggle && !disabled && !isToggling;
  const closeFinal = () => {
    if (!isSavingFinal) setIsFinalOpen(false);
  };

  return (
    <>
      <Label
        className={`rounded-3 border px-3 py-2 w-100 d-flex align-items-center justify-content-between gap-3 mb-0 user-select-none ${
          isLocked
            ? 'border-primary-subtle bg-primary-subtle text-primary'
            : 'border-success bg-success-subtle text-success shadow-sm'
        }`}
        style={{
          cursor: isInteractive ? 'pointer' : 'not-allowed',
          opacity: isInteractive ? 1 : 0.65,
        }}
      >
        <span className='fs-13 fw-semibold d-flex align-items-center gap-2'>
          <i
            className={`bx ${isLocked ? 'bx-lock-alt text-primary' : 'bx-lock-alt text-success'} fs-16`}
          ></i>
          {isLocked ? 'Agreed' : 'Agree & Lock'}
          {isToggling && <Spinner size='sm' />}
        </span>
        <div
          className='form-check form-switch mb-0 p-0 d-flex align-items-center'
          style={{ minHeight: 'auto' }}
        >
          <Input
            type='switch'
            role='switch'
            aria-checked={isLocked}
            checked={isLocked}
            disabled={!isInteractive}
            onChange={handleToggle}
            className='m-0 float-none'
            style={{ cursor: isInteractive ? 'pointer' : 'not-allowed' }}
          />
        </div>
      </Label>

      {isLocked && hasHtmlText(finalValue) && (
        <div className='mt-2 p-3 rounded-3 border border-primary-subtle bg-white'>
          <div className='fw-semibold fs-13 text-primary mb-1'>Final</div>
          <div
            className='text-dark fs-13 lh-base'
            dangerouslySetInnerHTML={{ __html: String(finalValue) }}
          />
        </div>
      )}

      {requiresFinalValue && canToggle && (
        <Modal
          isOpen={isFinalOpen}
          toggle={closeFinal}
          centered
          size='lg'
          backdrop='static'
        >
          <ModalHeader toggle={closeFinal}>Agree & Lock — Final</ModalHeader>
          <ModalBody>
            <p className='text-muted small mb-2'>
              Enter the final value agreed with this bidder. The original stays
              unchanged for other bidders.
            </p>
            <RespondNotesEditor
              id='agree-lock-final-value'
              value={finalDraft}
              onChange={e => setFinalDraft(e.target.value)}
            />
            {!hasHtmlText(finalDraft) && (
              <div className='text-danger small mt-2'>
                Final value is required.
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <ActionButton
              variant='secondary'
              onClick={closeFinal}
              disabled={isSavingFinal}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant='primary'
              onClick={handleSaveFinal}
              loading={isSavingFinal}
              disabled={isSavingFinal || !hasHtmlText(finalDraft)}
            >
              Agree and lock
            </ActionButton>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default AgreeLockButton;
